import { Tab, Tabs } from '@mui/material';
import { FC, useState } from 'react';

import { Lock, Notifications } from '@mui/icons-material';
import Heading from 'components/Heading';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { Row } from 'components/Layout';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import PageSection from 'components/PageSection';
import ReviewPayments from 'components/ReviewPayments';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import api from 'lib/trpcClient';
import { useDebounce } from 'usehooks-ts';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import PaymentFiltersAndSummary from '../PaymentFiltersAndSummary';

enum ReviewAccrualsTab {
  PayableAccruals = 'payableAccruals',
  ReceivableAccruals = 'receivableAccruals',
}

const tabsData = {
  [ReviewAccrualsTab.PayableAccruals]: {
    title: 'Payable accruals',
    icon: <Lock />,
  },
  [ReviewAccrualsTab.ReceivableAccruals]: {
    title: 'Receivable accruals',
    icon: <Notifications />,
  },
};

const pageId = 'review-accruals';

const ReviewAccruals: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const [viewDate, setViewDate] = useState<Date | null>(new Date());
  const [activeTab, setActiveTab] = useState<ReviewAccrualsTab>(
    ReviewAccrualsTab.PayableAccruals,
  );
  const pagination = usePagination({
    id: pageId,
    order: 'desc',
    orderBy: 'transactionDate',
  });

  const debouncedViewDate = useDebounce(viewDate, 800);

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const { data } = api.prepayments.getAccruals.useQuery({
    vehicleId: selectedVehicleId ?? '',
    viewDate: debouncedViewDate
      ? new CalendarDay(debouncedViewDate)
      : undefined,
    actionOperationType:
      activeTab === ReviewAccrualsTab.PayableAccruals
        ? ActionOperationType.Spent
        : ActionOperationType.Received,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  });

  const handleTabChange = (_: unknown, selectedTab: ReviewAccrualsTab) => {
    setActiveTab(selectedTab);
  };

  const rowsForTable = data?.results ?? [];

  const columns: Array<Column> = [
    {
      field: 'actionRef',
      headerName: 'Action',
      renderCell: ({ row }) => ActionRefLabels[row.actionRef],
      flex: 1,
      sortable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      sortable: false,
    },
    {
      field: 'transactionDate',
      headerName: 'Date',
      renderCell: ({ row }) => row.transactionDate.toLabel(),
      flex: 1,
    },
    {
      field: 'amountTcy',
      headerName: 'Amount currently accrued (TCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.unreconciledAmountTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'amountLcy',
      headerName: 'Amount currently accrued (LCY)',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.unreconciledAmountLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      flex: 1,
      sortable: false,
    },
    {
      field: 'counterpartyLabel',
      headerName: 'Counterparty',
      flex: 1,
      sortable: false,
    },
  ];

  return (
    <PageContainer heading="Automated Transactions: Review outstanding accruals">
      <PageContent withSpacing>
        <PaymentFiltersAndSummary
          title="Review outstanding accruals"
          viewDate={debouncedViewDate}
          onViewDateChange={(date) => {
            setViewDate(date);
          }}
        />

        <PageSection withSpacing>
          <Tabs
            showDivider
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {Object.keys(tabsData).map((tab) => (
              <Tab
                key={tab}
                value={tab}
                label={
                  <Row centered>
                    {tabsData[tab].icon}
                    <Heading variant="h5" noMargin>
                      {tabsData[tab].title}
                    </Heading>
                  </Row>
                }
              />
            ))}
          </Tabs>
          <BankReconciliationWarningBox
            date={debouncedViewDate}
            warningText="There are pending transactions awaiting bank reconciliation. Click below to process these before reviewing accruals, as reconciled transactions may change outstanding accruals."
          />
          <InteractiveTable
            id={pageId}
            columns={columns}
            rows={rowsForTable}
            pagination={pagination}
            scrollable
            fullHeight
            getExpandedContent={(row) => (
              <>
                <Row fullWidth>
                  <ReviewPayments
                    transactionId={row.id}
                    vehicleId={selectedVehicleId ?? ''}
                    currencyCode={row.currencyCode}
                    amountTcy={row.amountTcy}
                  />
                </Row>
                <Row fullWidth>
                  {!transactionTypesLoading && vehicle && (
                    <TransactionAllocator
                      showCurrencyRate
                      transactionTypeOptions={transactionTypeOptions}
                      transactions={row.transactions}
                      readOnly
                      onChange={() => {}}
                      transactionCurrency={row.currencyCode}
                      vehicle={vehicle}
                      fxRate={row.currencyRate}
                    />
                  )}
                </Row>
              </>
            )}
          />
        </PageSection>
      </PageContent>
    </PageContainer>
  );
};

export default ReviewAccruals;
